import * as React from 'react'

/**
 * Performance Overlay — mostra metricas em tempo real no canto inferior direito.
 * Visivel apenas em dev mode ou com ?perf=1 na URL.
 *
 * Props:
 *   renderCount (number) — quantas vezes o componente pai re-renderizou
 */
export default function PerfOverlay({ renderCount = 0 }) {
  const [fps, setFps] = React.useState(0)
  const [longTasks, setLongTasks] = React.useState(0)
  const [memory, setMemory] = React.useState(null)
  const [wsPerSec, setWsPerSec] = React.useState(0)

  // FPS counter via requestAnimationFrame
  React.useEffect(() => {
    let frameCount = 0
    let lastTime = performance.now()
    let rafId

    const tick = () => {
      frameCount++
      const now = performance.now()
      if (now - lastTime >= 1000) {
        setFps(frameCount)
        frameCount = 0
        lastTime = now
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [])

  // Long Tasks observer
  React.useEffect(() => {
    if (typeof PerformanceObserver === 'undefined') return
    let count = 0
    try {
      const observer = new PerformanceObserver((list) => {
        count += list.getEntries().length
        setLongTasks(count)
      })
      observer.observe({ type: 'longtask', buffered: true })
      return () => observer.disconnect()
    } catch {
      // longtask not supported
    }
  }, [])

  // Memory (Chrome only)
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (performance.memory) {
        setMemory(Math.round(performance.memory.usedJSHeapSize / 1048576))
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  // WebSocket messages per second
  React.useEffect(() => {
    let count = 0
    const handler = () => { count++ }
    window.addEventListener('alarms-new', handler)
    window.addEventListener('alarms-removed', handler)

    const interval = setInterval(() => {
      setWsPerSec(count)
      count = 0
    }, 1000)

    return () => {
      window.removeEventListener('alarms-new', handler)
      window.removeEventListener('alarms-removed', handler)
      clearInterval(interval)
    }
  }, [])

  const style = {
    position: 'fixed',
    bottom: 12,
    right: 12,
    zIndex: 99999,
    background: 'rgba(0,0,0,0.85)',
    color: '#0f0',
    fontFamily: 'monospace',
    fontSize: 11,
    padding: '8px 12px',
    borderRadius: 6,
    lineHeight: 1.6,
    pointerEvents: 'none',
    minWidth: 160,
  }

  return (
    <div style={style}>
      <div>FPS: <b style={{ color: fps < 30 ? '#f44' : '#0f0' }}>{fps}</b></div>
      <div>Renders: <b>{renderCount}</b></div>
      <div>Long Tasks: <b style={{ color: longTasks > 0 ? '#ff0' : '#0f0' }}>{longTasks}</b></div>
      {memory !== null && <div>Heap: <b>{memory} MB</b></div>}
      <div>WS msg/s: <b>{wsPerSec}</b></div>
    </div>
  )
}
